import {
	type ReactNode,
	createContext,
	useContext,
	useCallback,
	useState,
	useEffect,
	useMemo,
	useRef,
} from "react";
import { useAuth } from "@/lib/contexts/auth.context";
import OrgaAdminGruppeService from "@/lib/orgaAdmin/orgaAdminGruppe.service";
import type {
	OrgaAdminGruppeWithAssignmentStatistics,
	StudentKlausurDetailedDTO,
	OrgaAdminGruppeDTO,
} from "@/lib/orgaAdmin/orgaAdmin.types";
import { KorrekturStepStatus } from "@/lib/orgaAdmin/orgaAdmin.types";
import {
	findDuplicateKorrektorStepNumbers,
	getStepByNumber,
} from "../utils/assignmentRules";
import { toast } from "sonner";
import type {
	OrgaAdminUser,
	GruppeKlausurDTO,
} from "@/lib/orgaAdmin/orgaAdmin.types";
import { CompletedKorrekturStepsSkippedDialog } from "../components/completedKorrekturStepsSkippedDialog.component";
import { BulkAssignmentProgressDialog } from "../components/bulkAssignmentProgressDialog.component";

/**
 * Assignment filters for the korrektur status table
 */
interface AssignmentFilters {
	searchTerm?: string;
	korrektorId?: string;
	stepNumber?: number;
	openBulkAssignment?: boolean;
}

/**
 * Context type for korrektur step assignment operations
 */
interface KorrekturStepAssignmentContextType {
	/**
	 * Assigns a korrektor to a specific korrektur step
	 * @param korrekturStepId - The ID of the korrektur step to assign
	 * @param korrektorId - The ID of the korrektor to assign (use "null" to remove assignment)
	 */
	assignKorrekturStepToKorrektor: (
		korrekturStepId: string,
		korrektorId: string,
	) => Promise<void>;

	/**
	 * Get statistic for a group id (as example after updating a assignment)
	 * @param groupId
	 * @returns
	 */
	getStatisticsForGroup: (
		groupId: string,
	) => Promise<OrgaAdminGruppeWithAssignmentStatistics>;

	/**
	 * Get student klausuren for a group id
	 * @param groupId
	 * @returns
	 */
	studentKlausuren: StudentKlausurDetailedDTO[];

	/**
	 * Indicates whether student klausuren are currently being loaded
	 */
	isLoading: boolean;

	/**
	 * Assigns multiple korrektur steps to a korrektor in a group or remove them
	 * @param korrekturStepIds - The IDs of the korrektur steps to assign
	 * @param korrektorId - The ID of the korrektor to assign (use "null" to remove assignment)
	 */
	assignBulkKorrekturStepsToKorrektor: (
		korrekturStepIds: string[],
		korrektorId: string,
	) => Promise<void>;

	/**
	 * Available step numbers across all student klausuren
	 */
	availableStepNumbers: number[];

	/**
	 * Plan a bulk assignment/removal and compute affected items and target step IDs
	 */
	planBulkAssignment: (
		stepNr: number,
		selectedStudentKlausurIds: string[],
		targetKorrektor: string,
	) => {
		isRemoval: boolean;
		totalSelected: number;
		affectedAssignments: Array<{
			klausurId: string;
			internalIdentifier: string;
			currentKorrektorId: string | null;
		}>;
		duplicateConflicts: Array<{
			klausurId: string;
			internalIdentifier: string;
			conflictingStepNumbers: number[];
		}>;
		targetStepIds: string[];
		validSelectionCount: number;
	};

	/**
	 * Removes a korrektur step from a korrektor in a group
	 * @param korrekturStepId - The ID of the korrektur step to remove
	 *
	 * THIS IS JUST A WRAPPER FOR NULL AS KORREKTORID
	 */
	removeKorrekturStepFromKorrektor: (korrekturStepId: string) => Promise<void>;

	/**
	 * Assignment filters for the korrektur status table
	 */
	assignmentFilters: AssignmentFilters | null;

	/**
	 * Set assignment filters to navigate to korrektur status table with specific filters
	 */
	setAssignmentFilters: (filters: AssignmentFilters | null) => void;

	/**
	 * Clear assignment filters
	 */
	clearAssignmentFilters: () => void;

	/**
	 * Users in the current group
	 */
	users: OrgaAdminUser[];

	/**
	 * Klausuren in the current group
	 */
	klausuren: GruppeKlausurDTO[];
}

/**
 * Context for korrektur step assignment operations
 *
 * This context is used to assign and remove korrektors from korrektur steps.
 * It is wrapped in a provider component that provides the context to the rest of the application.
 */
const KorrekturStepAssignmentContext = createContext<
	KorrekturStepAssignmentContextType | undefined
>(undefined);

/**
 * Props for the KorrekturStepAssignmentProvider
 * @param children - The children to render
 * @param groupId - The ID of the group
 * @param selectedBackendGroup - The selected backend group
 * @param onOperationComplete - The function to call when the operation is complete
 */
interface KorrekturStepAssignmentProviderProps {
	children: ReactNode;
	groupId: string;
	selectedBackendGroup?: OrgaAdminGruppeDTO;
	onOperationComplete?: () => void;
}

/**
 * Provider component that handles korrektur step assignment operations
 * Uses the current organization from auth context and the provided groupId
 */
export function KorrekturStepAssignmentProvider({
	children,
	groupId,
	selectedBackendGroup,
	onOperationComplete,
}: KorrekturStepAssignmentProviderProps) {
	const { currentOrganization } = useAuth();
	const [studentKlausuren, setStudentKlausuren] = useState<
		StudentKlausurDetailedDTO[]
	>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [assignmentFilters, setAssignmentFilters] =
		useState<AssignmentFilters | null>(null);
	const [completedStepsDialog, setCompletedStepsDialog] = useState<{
		open: boolean;
		skippedCount: number;
	} | null>(null);
	const [bulkAssignmentProgress, setBulkAssignmentProgress] = useState<{
		isOpen: boolean;
		completed: number;
		total: number;
		isRemoving: boolean;
	}>({
		isOpen: false,
		completed: 0,
		total: 0,
		isRemoving: false,
	});

	const completedStepsDialogResolveRef = useRef<(() => void) | null>(null);

	const availableStepNumbers = useMemo(() => {
		if (studentKlausuren.length === 0) return [] as number[];
		const allStepNumbers = new Set<number>();
		studentKlausuren.forEach((klausur) => {
			klausur.korrekturSteps.forEach((step) => {
				allStepNumbers.add(step.stepNr);
			});
		});
		return Array.from(allStepNumbers).sort((a, b) => a - b);
	}, [studentKlausuren]);

	const korrekturStepStatusById = useMemo(() => {
		const map = new Map<string, KorrekturStepStatus>();
		for (const klausur of studentKlausuren) {
			for (const step of klausur.korrekturSteps) {
				map.set(step.id, step.status);
			}
		}
		return map;
	}, [studentKlausuren]);

	/**
	 * Shows the completed steps skipped dialog
	 * @param skippedCount - The number of skipped steps
	 * @returns A promise that resolves when the dialog is closed
	 */
	const showCompletedStepsSkippedDialog = useCallback(
		(skippedCount: number) => {
			if (skippedCount <= 0) return Promise.resolve();

			return new Promise<void>((resolve) => {
				completedStepsDialogResolveRef.current = () => {
					completedStepsDialogResolveRef.current = null;
					setCompletedStepsDialog(null);
					resolve();
				};
				setCompletedStepsDialog({ open: true, skippedCount });
			});
		},
		[],
	);

	/**
	 * Refreshes the student klausuren for the group
	 */
	const refreshStudentKlausuren = useCallback(async () => {
		if (!currentOrganization) {
			throw new Error("No organization selected");
		}
		setIsLoading(true);
		try {
			const klausuren =
				await OrgaAdminGruppeService.getInstance().getStudentKlausurenInGruppe(
					currentOrganization.organizationId,
					groupId,
				);
			setStudentKlausuren(klausuren);
		} finally {
			setIsLoading(false);
		}
	}, [currentOrganization, groupId]);

	useEffect(() => {
		if (currentOrganization) {
			refreshStudentKlausuren();
		}
	}, [currentOrganization, refreshStudentKlausuren]);

	/**
	 * Gets the statistics for the group
	 */
	const getStatisticsForGroup =
		useCallback(async (): Promise<OrgaAdminGruppeWithAssignmentStatistics> => {
			if (!currentOrganization) {
				throw new Error("No organization selected");
			}
			const statistics =
				await OrgaAdminGruppeService.getInstance().getStatisticsForGruppe(
					currentOrganization.organizationId,
					groupId,
				);

			return statistics;
		}, [currentOrganization, groupId]);

	/**
	 * Assigns multiple korrektur steps to a korrektor in a group or remove them
	 * @param korrekturStepIds - The IDs of the korrektur steps to assign
	 * @param korrektorId - The ID of the korrektor to assign (use "null" to remove assignment)
	 */
	const assignBulkKorrekturStepsToKorrektor = useCallback(
		async (korrekturStepIds: string[], korrektorId: string) => {
			if (!currentOrganization) {
				throw new Error("No organization selected");
			}

			const completedStepIds = korrekturStepIds.filter(
				(id) =>
					korrekturStepStatusById.get(id) === KorrekturStepStatus.COMPLETED,
			);
			const completedStepIdSet = new Set(completedStepIds);
			const applicableStepIds = korrekturStepIds.filter(
				(id) => !completedStepIdSet.has(id),
			);

			if (completedStepIds.length > 0) {
				await showCompletedStepsSkippedDialog(completedStepIds.length);
			}

			try {
				// Nothing left to do after filtering out completed steps.
				if (applicableStepIds.length === 0) {
					return;
				}

				// Show progress dialog for bulk operations
				const isRemoving = korrektorId === "null";
				setBulkAssignmentProgress({
					isOpen: true,
					completed: 0,
					total: applicableStepIds.length,
					isRemoving,
				});

				await OrgaAdminGruppeService.getInstance().assignKorrekturStepsToKorrektor(
					currentOrganization.organizationId,
					groupId,
					applicableStepIds,
					korrektorId,
					(completed, total) => {
						// Update progress as each step completes
						setBulkAssignmentProgress({
							isOpen: true,
							completed,
							total,
							isRemoving,
						});
					},
				);

				// Call the completion callback to refresh data
				onOperationComplete?.();
			} catch (error) {
				toast.error(
					"Die Aktion konnte nicht ausgeführt werden, bitte versuche es erneut.",
				);
				console.error("Failed to assign korrektur steps to korrektor:", error);
				throw error;
			} finally {
				// Hide progress dialog
				setBulkAssignmentProgress({
					isOpen: false,
					completed: 0,
					total: 0,
					isRemoving: false,
				});
				refreshStudentKlausuren();
			}
		},
		[
			currentOrganization,
			groupId,
			korrekturStepStatusById,
			onOperationComplete,
			refreshStudentKlausuren,
			showCompletedStepsSkippedDialog,
		],
	);

	/**
	 * Removes a korrektur step from a korrektor in a group
	 * @param korrekturStepId - The ID of the korrektur step to remove
	 */
	const removeKorrekturStepFromKorrektor = useCallback(
		async (korrekturStepId: string) => {
			if (!currentOrganization) {
				throw new Error("No organization selected");
			}

			await OrgaAdminGruppeService.getInstance().removeKorrekturStepFromKorrektor(
				currentOrganization.organizationId,
				groupId,
				korrekturStepId,
			);

			onOperationComplete?.();
		},
		[currentOrganization, groupId, onOperationComplete],
	);

	/**
	 * Assigns a korrektor to a korrektur step
	 * Handles both assignment and removal (when korrektorId is "null")
	 *
	 * @note To remove a korrektor from a korrekturstep just call the korrekturstep with null as korrekturStepId
	 */
	const assignKorrekturStepToKorrektor = useCallback(
		async (korrekturStepId: string, korrektorId: string) => {
			if (!currentOrganization) {
				throw new Error("No organization selected");
			}

			if (
				korrekturStepStatusById.get(korrekturStepId) ===
				KorrekturStepStatus.COMPLETED
			) {
				await showCompletedStepsSkippedDialog(1); // one step, one skipped :D
				return;
			}

			try {
				await OrgaAdminGruppeService.getInstance().assignKorrekturStepToKorrektor(
					currentOrganization.organizationId,
					groupId,
					korrekturStepId,
					korrektorId,
				);

				// Call the completion callback to refresh data
				onOperationComplete?.();
			} catch (error) {
				onOperationComplete?.();
				toast.error(
					"Die Aktion konnte nicht ausgeführt werden, bitte versuche es erneut.",
				);
				console.error("Failed to assign korrektor to step:", error);
				throw error;
			} finally {
				refreshStudentKlausuren();
			}
		},

		[
			currentOrganization,
			groupId,
			korrekturStepStatusById,
			onOperationComplete,
			refreshStudentKlausuren,
			showCompletedStepsSkippedDialog,
		],
	);

	// Filter management functions
	const clearAssignmentFilters = useCallback(() => {
		setAssignmentFilters(null);
	}, []);

	const contextValue: KorrekturStepAssignmentContextType = {
		assignKorrekturStepToKorrektor,
		getStatisticsForGroup,
		studentKlausuren,
		isLoading,
		assignBulkKorrekturStepsToKorrektor,
		removeKorrekturStepFromKorrektor,
		availableStepNumbers,
		assignmentFilters,
		setAssignmentFilters,
		clearAssignmentFilters,
		users: selectedBackendGroup?.users || [],
		klausuren: selectedBackendGroup?.klausuren || [],
		planBulkAssignment: (
			stepNr,
			selectedStudentKlausurIds,
			targetKorrektor,
		) => {
			const isRemoval = targetKorrektor === "remove";
			const selectedKlausuren = studentKlausuren.filter((k) =>
				selectedStudentKlausurIds.includes(k.id),
			);
			const validKlausuren = selectedKlausuren.filter((klausur) =>
				klausur.korrekturSteps.some((step) => step.stepNr === stepNr),
			);

			const affectedAssignments: Array<{
				klausurId: string;
				internalIdentifier: string;
				currentKorrektorId: string | null;
			}> = [];

			// When assigning, check duplicates across other steps in the same klausur
			const duplicateConflicts: Array<{
				klausurId: string;
				internalIdentifier: string;
				conflictingStepNumbers: number[];
			}> = [];

			validKlausuren.forEach((klausur) => {
				const targetStep = getStepByNumber(klausur, stepNr);
				const hasAssignment = Boolean(
					targetStep?.korrektorId &&
						targetStep.korrektorId !== "" &&
						targetStep.korrektorId !== "null",
				);
				if ((isRemoval && hasAssignment) || (!isRemoval && hasAssignment)) {
					affectedAssignments.push({
						klausurId: klausur.id,
						internalIdentifier: klausur.internalIdentifier,
						currentKorrektorId: targetStep?.korrektorId ?? null,
					});
				}

				// When assigning, check duplicates across other steps in the same klausur
				if (!isRemoval && targetKorrektor && targetKorrektor !== "") {
					const conflictingStepNumbers = findDuplicateKorrektorStepNumbers(
						klausur,
						stepNr,
						targetKorrektor,
					);
					if (conflictingStepNumbers.length > 0) {
						duplicateConflicts.push({
							klausurId: klausur.id,
							internalIdentifier: klausur.internalIdentifier,
							conflictingStepNumbers,
						});
					}
				}
			});

			// Only include steps that won't cause duplicates when assigning
			const targetStepIds = validKlausuren
				.filter((klausur) => {
					if (isRemoval) return true;
					const conflict = duplicateConflicts.find(
						(dc) => dc.klausurId === klausur.id,
					);
					return !conflict;
				})
				.map((klausur) => getStepByNumber(klausur, stepNr)?.id)
				.filter((id): id is string => Boolean(id));

			return {
				isRemoval,
				totalSelected: selectedStudentKlausurIds.length,
				affectedAssignments,
				duplicateConflicts,
				targetStepIds,
				validSelectionCount: validKlausuren.length,
			};
		},
	};

	return (
		<KorrekturStepAssignmentContext.Provider value={contextValue}>
			{children}
			<CompletedKorrekturStepsSkippedDialog
				open={Boolean(completedStepsDialog?.open)}
				skippedCount={completedStepsDialog?.skippedCount ?? 0}
				onContinue={() => completedStepsDialogResolveRef.current?.()}
			/>
			<BulkAssignmentProgressDialog
				isOpen={bulkAssignmentProgress.isOpen}
				totalSteps={bulkAssignmentProgress.total}
				completedSteps={bulkAssignmentProgress.completed}
				isRemoving={bulkAssignmentProgress.isRemoving}
			/>
		</KorrekturStepAssignmentContext.Provider>
	);
}

/**
 * Hook to access korrektur step assignment operations
 * Must be used within a KorrekturStepAssignmentProvider
 */
export function useKorrekturStepAssignmentOperations(): KorrekturStepAssignmentContextType {
	const context = useContext(KorrekturStepAssignmentContext);
	if (context === undefined) {
		throw new Error(
			"useKorrekturStepAssignmentOperations must be used within a KorrekturStepAssignmentProvider",
		);
	}
	return context;
}
